/**
 * How a file a user attaches in Coco chat is routed. Two disjoint destinations:
 *
 * - **tabular** (csv/tsv/json/ndjson/xlsx/xls) → a chat-scoped dataset, via the
 *   dataset flow. See `apps/client/.../upload-chat-dataset.ts`.
 * - **ingot document** (pdf/text/markdown/html/pptx) → the chat's ingot, queried
 *   by `coco/ingot-query`. See `apps/server/.../ingot/chat-ingot.service.ts`.
 *
 * One module so "uploaded", "tool loaded" and "clone on copy" (Ingot layers 2–4)
 * all decide from the same predicate and can't drift. Shared from `@cotera/api`
 * so the client, server and SDK all read the same rule.
 */

/** A user-attached file part, as far as routing needs to see it. */
export type ChatFilePart = {
  mediaType?: string;
  filename?: string;
};

/**
 * File kinds routed into a chat-scoped dataset instead of being inlined or sent
 * to Ingot: CSV/TSV, JSON/NDJSON, and Excel (.xlsx/.xls). All parsed server-side.
 */
const TABULAR_EXTENSIONS = [
  '.csv',
  '.tsv',
  '.json',
  '.ndjson',
  '.xlsx',
  '.xls',
];
const TABULAR_MEDIA_TYPE_HINTS = [
  'csv',
  'tab-separated-values',
  'json',
  'spreadsheetml',
  'ms-excel',
];

/** Whether an attachment should become a chat dataset rather than a file part. */
export const isTabularFilePart = (part: ChatFilePart): boolean => {
  const mediaType = (part.mediaType ?? '').toLowerCase();
  if (TABULAR_MEDIA_TYPE_HINTS.some((hint) => mediaType.includes(hint))) {
    return true;
  }
  const filename = (part.filename ?? '').toLowerCase();
  return TABULAR_EXTENSIONS.some((ext) => filename.endsWith(ext));
};

/**
 * Media types Ingot can parse. Mirrors
 * `apps/ingot/src/contexts/files/domain/media-type.ts`, minus `text/csv` which
 * goes to the dataset flow instead.
 */
export const INGOT_DOCUMENT_MEDIA_TYPES = [
  'application/pdf',
  'text/plain',
  'text/markdown',
  'text/html',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
] as const;

/**
 * Extension → canonical Ingot media type. Browsers often send an empty or
 * generic type for `.md` and friends, so the extension is the fallback.
 */
const INGOT_EXTENSION_MEDIA_TYPES: Readonly<Record<string, string>> = {
  '.pdf': 'application/pdf',
  '.txt': 'text/plain',
  '.md': 'text/markdown',
  '.markdown': 'text/markdown',
  '.html': 'text/html',
  '.htm': 'text/html',
  '.pptx':
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
};

const INGOT_DOCUMENT_MEDIA_TYPE_SET: ReadonlySet<string> = new Set(
  INGOT_DOCUMENT_MEDIA_TYPES
);

/** The media type before any `; charset=…` suffix, lowercased. */
const bareMediaType = (mediaType?: string): string =>
  (mediaType ?? '').split(';')[0]!.trim().toLowerCase();

const extensionOf = (filename?: string): string | null => {
  const name = (filename ?? '').toLowerCase();
  const dot = name.lastIndexOf('.');
  return dot === -1 ? null : name.slice(dot);
};

/**
 * The canonical Ingot media type for an attachment, or null when Ingot can't
 * parse it. Prefers the part's own media type, falling back to the extension —
 * so a `.md` that arrives with an empty or generic type still uploads as
 * `text/markdown`.
 */
export const ingotMediaTypeForPart = (part: ChatFilePart): string | null => {
  const declared = bareMediaType(part.mediaType);
  if (INGOT_DOCUMENT_MEDIA_TYPE_SET.has(declared)) {
    return declared;
  }
  const ext = extensionOf(part.filename);
  if (ext !== null && ext in INGOT_EXTENSION_MEDIA_TYPES) {
    return INGOT_EXTENSION_MEDIA_TYPES[ext]!;
  }
  return null;
};

/**
 * A user-attached file routed to Ingot: not tabular, and a type Ingot parses.
 * Layers 2 (upload), 3 (tool loading) and 4 (clone on copy) all gate on this.
 */
export const isIngotDocumentFilePart = (part: ChatFilePart): boolean =>
  !isTabularFilePart(part) && ingotMediaTypeForPart(part) !== null;
